import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { MessageSquare, Trash2, BellPlus, CalendarClock, X } from "lucide-react";

function formatTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

/* -------------------- Notes (Locker Room) -------------------- */
export function NotesPanel({ contactId, currentUser }) {
  const [notes, setNotes] = useState([]);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/contacts/${contactId}/notes`);
      setNotes(data);
    } catch {}
  };
  useEffect(() => { load(); }, [contactId]);

  const submit = async () => {
    if (!body.trim()) return;
    setBusy(true);
    try {
      await api.post(`/contacts/${contactId}/notes`, { body: body.trim() });
      setBody("");
      load();
      toast.success("Note added");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this note?")) return;
    try { await api.delete(`/notes/${id}`); load(); } catch {}
  };

  return (
    <div className="border-t border-stone-200 pt-4" data-testid="notes-panel">
      <div className="flex items-center gap-2 mb-3">
        <MessageSquare className="h-4 w-4 text-[#B85C38]" />
        <h3 className="font-display font-semibold tracking-tight">Notes (Locker Room)</h3>
        <span className="text-[10px] font-mono uppercase tracking-wider text-stone-500 ml-auto">
          {notes.length} {notes.length === 1 ? "note" : "notes"}
        </span>
      </div>

      <div className="space-y-2 mb-3">
        <Textarea
          placeholder="Add a note. Use @firstname to tag a teammate."
          value={body}
          onChange={(e) => setBody(e.target.value)}
          className="rounded-sm border-stone-300 text-sm"
          rows={2}
          data-testid="note-input"
        />
        <div className="flex justify-end">
          <Button
            onClick={submit}
            disabled={busy || !body.trim()}
            size="sm"
            className="rounded-sm bg-[#B85C38] hover:bg-[#9A4D2E] text-white"
            data-testid="note-submit-btn"
          >
            Add note
          </Button>
        </div>
      </div>

      {notes.length === 0 ? (
        <div className="text-sm text-stone-500 italic">No notes yet.</div>
      ) : (
        <ul className="space-y-2" data-testid="notes-list">
          {notes.map((n) => {
            const canDelete = currentUser?.role === "admin" || n.user_id === currentUser?.id;
            return (
              <li key={n.id} className="border border-stone-200 rounded-sm p-2.5 bg-stone-50">
                <div className="flex items-baseline justify-between gap-2 mb-1">
                  <span className="text-xs font-medium text-stone-900">{n.user_name}</span>
                  <span className="text-[10px] font-mono text-stone-500">{formatTime(n.created_at)}</span>
                </div>
                <div className="text-sm text-stone-800 whitespace-pre-wrap">{highlightMentions(n.body)}</div>
                {canDelete && (
                  <button
                    onClick={() => remove(n.id)}
                    className="mt-1 text-[11px] text-red-600 hover:text-red-800 inline-flex items-center gap-1"
                    data-testid={`delete-note-${n.id}`}
                  >
                    <Trash2 className="h-3 w-3" /> Delete
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function highlightMentions(text) {
  const parts = text.split(/(@[A-Za-z][A-Za-z0-9._-]{1,})/g);
  return parts.map((p, i) =>
    p.startsWith("@") ? (
      <span key={i} className="text-[#B85C38] font-medium">{p}</span>
    ) : (
      p
    )
  );
}

/* -------------------- Reminder (Snap Count) -------------------- */
export function ReminderPanel({ contactId }) {
  const [date, setDate] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  const submit = async () => {
    if (!date) return;
    setBusy(true);
    try {
      await api.post(`/contacts/${contactId}/reminders`, { remind_date: date, note });
      toast.success(`Reminder set for ${date}`);
      setDate(""); setNote(""); setOpen(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="border border-stone-200 rounded-sm p-3 w-full text-left flex items-center gap-2 hover:bg-stone-50"
        data-testid="reminder-open-btn"
      >
        <BellPlus className="h-4 w-4 text-[#B85C38]" />
        <span className="text-sm font-medium">Set a follow-up reminder</span>
      </button>
    );
  }

  return (
    <div className="border border-stone-200 rounded-sm p-3 bg-stone-50" data-testid="reminder-panel">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <CalendarClock className="h-4 w-4 text-[#B85C38]" />
          <span className="text-sm font-semibold">Follow-up reminder</span>
        </div>
        <button onClick={() => setOpen(false)} className="text-stone-400 hover:text-stone-700">
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="space-y-2">
        <Input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="h-9 rounded-sm border-stone-300"
          data-testid="reminder-date-input"
        />
        <Input
          placeholder="Note (optional) — e.g. 'Try again'"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          className="h-9 rounded-sm border-stone-300 text-sm"
          data-testid="reminder-note-input"
        />
        <Button
          onClick={submit}
          disabled={busy || !date}
          size="sm"
          className="w-full rounded-sm bg-[#B85C38] hover:bg-[#9A4D2E] text-white"
          data-testid="reminder-save-btn"
        >
          Set reminder
        </Button>
      </div>
    </div>
  );
}
